<script lang="ts">
export default {
  name: 'KkFilterModal',
}
</script>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { KkModal, KkCheckbox, KkButton } from '../../base'
import { toNumberLocaleString } from '../../../modules/localeString'
import type { FilterItem, AllFormValue, Kind } from '../../../types'

export interface Props {
  isVisible: boolean
  filterItems: FilterItem[]
  formValues: Partial<AllFormValue>
  totalCount?: number
  submitButtonText?: string
}

export interface Emits {
  (key: 'close'): void
  (key: 'reset'): void
  (key: 'submit'): void
  (key: 'change-check-box', value: { kind: Kind, id: string | number }): void
}

const props = withDefaults(defineProps<Props>(), {
  totalCount: -1,
  submitButtonText: '',
})
const emits = defineEmits<Emits>()
const { t } = useI18n()

const submitText = computed(() => props.submitButtonText || t('labels.submitFilterCond'))

const numberLocaleString = (value: number) => toNumberLocaleString(value)

const handleCheckChange = (kind: Kind, id: string | number) => {
  emits('change-check-box', { kind, id })
}
</script>

<template>
  <KkModal :is-visible="props.isVisible" @change="$emit('close')">
    <div class="content__header">
      {{ $t('labels.selectFilterCond') }}
    </div>
    <div class="content__body">
      <template v-for="item in props.filterItems" :key="item.type">
        <div class="filterItem">
          <div class="filterItem__header">{{ item.filterName }}</div>
          <ul class="filterItem__body">
            <li v-for="tag in item.tags" :key="`${item.kind}_${tag.id}`" class="checkbox__item">
              <KkCheckbox :id="`${item.kind}_${tag.id}`"
                          :label="`${tag.name}`"
                          :model-value="(props.formValues[item.kind] as any)?.includes(tag.id)"
                          @update:modelValue="handleCheckChange(item.kind, tag.id)"
              />
            </li>
          </ul>
        </div>
      </template>
    </div>
    <div class="content__footer">
      <div v-if="props.totalCount >= 0" class="storeCountWrapper">
        <i18n-t keypath="components.FilterModal.count">
          <span class="storeCount">
            {{ numberLocaleString(props.totalCount) }}
          </span>
        </i18n-t>
      </div>
      <div class="buttonGroup">
        <div class="buttonGroup__item">
          <KkButton color="secondary" outlined :text="$t('labels.resetFilterCond')" @click="$emit('reset')" />
        </div>
        <div class="buttonGroup__item">
          <KkButton color="primary" :text="submitText" @click="$emit('submit')" />
        </div>
      </div>
    </div>
  </KkModal>
</template>

<style scoped lang="scss">
@import "../../../../../stylesheets/biz_spa/foundation/_var.scss";
.content {
  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 20px 32px;
    border-bottom: 1px solid $color-base-c;
    font-size: 16px;
    line-height: 1.5;
  }
  &__body {
    height: 50vh;
    padding: 32px;
    overflow-y: scroll;
  }
  &__footer {
    display: flex;
    justify-content: flex-end;
    padding: 32px;
    border-top: 1px solid $color-base-c;
    @media screen and (max-width: 768px) {
      display: block;
    }
  }
}
.buttonGroup {
  display: flex;
  flex-grow: 0;
  &__item {
    margin: 8px;
  }
  @media screen and (max-width: 768px) {
    justify-content: flex-end;
  }
  @media screen and (max-width: 340px) {
    justify-content: center;
  }
}
.filterItem {
  $root: #{&};
  display: flex;
  border: 1px solid $color-base-c;
  border-radius: 4px;
  box-shadow: 0 0 0 rgba(63, 63, 68, .05);
  background: #FFF;
  &:not(:first-child) {
    margin-top: 12px;
  }
  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 20%;
    padding: 24px;
    background: #F5F6F9;
    font-weight: bold;
    border-bottom: 1px solid $color-base-c;
  }
  &__body {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 80%;
    padding: 20px;
  }
}
.checkbox {
  &__item {
    padding: 12px;
  }
}
.storeCountWrapper {
  margin-right: 10px;
  .storeCount {
    margin: 0 5px;
    color: $color-accent-primary;
    font-weight: 700;
    font-size: 30px;
  }
}
</style>
